import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { API } from '../App';
import { Ship, Filter, RefreshCw, Activity, History, FileText, X, ChevronDown, ChevronUp, Download, Clock } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Custom vessel icon based on ship type
const createVesselIcon = (type, heading = 0) => {
  const colors = {
    cargo: '#FFD60A',
    tanker: '#FF3B30',
    passenger: '#00F0FF',
    fishing: '#00FF9D',
    military: '#FF3B30',
    sailing: '#FFFFFF',
    other: '#71717a'
  };
  
  const color = colors[type.toLowerCase()] || colors.other;
  
  return L.divIcon({
    className: 'vessel-marker',
    html: `<div style="transform: rotate(${heading}deg); font-size: 20px; color: ${color}; filter: drop-shadow(0 0 3px rgba(0,0,0,0.8));">▲</div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

// MapUpdater removed - auto-zoom disabled to give user manual control
// User can now freely pan and zoom the map without automatic repositioning

export default function VesselsView() {
  const [vessels, setVessels] = useState([]);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [vesselDetails, setVesselDetails] = useState(null);
  const [historicalTrack, setHistoricalTrack] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [countdown, setCountdown] = useState(30);
  const [filters, setFilters] = useState({
    type: 'All',
    flag: 'All',
    minLength: 0
  });

  const loadVessels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vessels/live`);
      setVessels(response.data.vessels || []);
      setLastUpdate(new Date());
      setCountdown(30);
    } catch (error) {
      console.error('Error loading vessels:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVessels();
  }, [loadVessels]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const refreshInterval = setInterval(() => {
      loadVessels();
    }, 30000);
    
    const countdownInterval = setInterval(() => {
      setCountdown(prev => prev > 0 ? prev - 1 : 30);
    }, 1000);
    
    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [autoRefresh, loadVessels]);

  const loadVesselDetails = async (mmsi) => {
    try {
      const response = await axios.get(`${API}/vessels/${mmsi}/details`);
      setVesselDetails(response.data);
      
      // Load historical track
      const trackResponse = await axios.get(`${API}/vessels/${mmsi}/track`);
      setHistoricalTrack(trackResponse.data.track || []);
    } catch (error) {
      console.error('Error loading vessel details:', error);
    }
  };

  const handleVesselClick = (vessel) => {
    setSelectedVessel(vessel);
    loadVesselDetails(vessel.mmsi);
  };

  const closeDetails = () => {
    setSelectedVessel(null);
    setVesselDetails(null);
    setHistoricalTrack([]);
  };

  // Download vessels data as JSON
  const downloadVesselsData = () => {
    const dataStr = JSON.stringify(vessels, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `DefenseWatch_Vessels_${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Download vessel details as JSON
  const downloadVesselDetails = () => {
    if (!vesselDetails) return;
    const dataStr = JSON.stringify({ vessel: vesselDetails, track: historicalTrack }, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `DefenseWatch_Vessel_${vesselDetails.mmsi}_${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const filteredVessels = vessels.filter(vessel => {
    if (filters.type !== 'All' && vessel.type !== filters.type) return false;
    if (filters.flag !== 'All' && vessel.flag !== filters.flag) return false;
    if (vessel.length < filters.minLength) return false;
    return true;
  });

  return (
    <div className="relative w-full h-full" data-testid="vessels-view">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-[1000] glass-panel border-b border-white/10 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Ship className="w-6 h-6 text-[#00FF9D]" />
              MARITIME VESSEL TRACKING
            </h1>
            <p className="text-xs text-zinc-400 font-mono mt-1">
              Real-time AIS data • Auto-refresh: {autoRefresh ? `${countdown}s` : 'OFF'}
              {lastUpdate && ` • Last update: ${lastUpdate.toLocaleTimeString()}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                autoRefresh ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50' : 'bg-white/5 text-zinc-400 border border-white/10'
              }`}
              title={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            >
              <Clock className="w-4 h-4" />
              <span>{autoRefresh ? '30s' : 'OFF'}</span>
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                showFilters ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50' : 'bg-white/5 text-zinc-400 border border-white/10'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>FILTERS</span>
            </button>
            <button
              onClick={downloadVesselsData}
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10 transition-all"
              title="Download all vessels data"
              data-testid="download-vessels-btn"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={loadVessels}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-[#00FF9D]/10 border border-[#00FF9D]/30">
              <Activity className="w-4 h-4 text-[#00FF9D]" />
              <span className="text-xs font-mono text-[#00FF9D]">{filteredVessels.length} VESSELS</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="absolute top-24 right-4 z-[1000] w-80 glass-panel tactical-border p-4">
          <h3 className="text-sm font-bold uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Vessel Filters</h3>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">Vessel Type</label>
              <select 
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
                className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 text-sm font-mono"
              >
                <option>All</option>
                <option>Cargo</option>
                <option>Tanker</option>
                <option>Passenger</option>
                <option>Fishing</option>
                <option>Military</option>
                <option>Sailing</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">Minimum Length (m)</label>
              <input
                type="number"
                value={filters.minLength}
                onChange={(e) => setFilters({...filters, minLength: parseInt(e.target.value) || 0})}
                className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 text-sm font-mono"
                placeholder="0"
              />
            </div>
          </div>
        </div>
      )}

      {/* Vessel Details Modal */}
      {selectedVessel && (
        <div className="absolute top-24 left-4 z-[1000] w-96 max-h-[calc(100vh-120px)] overflow-y-auto glass-panel tactical-border p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed' }}>VESSEL IDENTIFICATION</h2>
            <div className="flex items-center gap-2">
              <button 
                onClick={downloadVesselDetails}
                className="text-zinc-500 hover:text-[#00FF9D] transition-colors"
                title="Download vessel data"
                data-testid="download-vessel-details-btn"
              >
                <Download className="w-5 h-5" />
              </button>
              <button onClick={closeDetails} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {vesselDetails ? (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="p-3 bg-black/20 border border-white/10">
                <h3 className="text-sm font-bold text-[#00FF9D] mb-2">{vesselDetails.name}</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-zinc-500 font-mono">MMSI:</span>
                    <p className="text-white font-mono">{vesselDetails.mmsi}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500 font-mono">IMO:</span>
                    <p className="text-white font-mono">{vesselDetails.imo}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500 font-mono">Type:</span>
                    <p className="text-white">{vesselDetails.type}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500 font-mono">Flag:</span>
                    <p className="text-white">{vesselDetails.flag}</p>
                  </div>
                </div>
              </div>

              {/* Current Status */}
              <div className="p-3 bg-black/20 border border-white/10">
                <h3 className="text-xs font-bold uppercase mb-2 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#00FF9D]" />
                  Current Status
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Position:</span>
                    <span className="text-white font-mono">{vesselDetails.lat.toFixed(4)}°, {vesselDetails.lon.toFixed(4)}°</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Speed:</span>
                    <span className="text-white font-mono">{vesselDetails.speed} knots</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Course:</span>
                    <span className="text-white font-mono">{vesselDetails.course}°</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Heading:</span>
                    <span className="text-white font-mono">{vesselDetails.heading}°</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Destination:</span>
                    <span className="text-white">{vesselDetails.destination || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Vessel Specifications */}
              <div className="p-3 bg-black/20 border border-white/10">
                <h3 className="text-xs font-bold uppercase mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#00FF9D]" />
                  Specifications
                </h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-zinc-500">Length:</span>
                    <p className="text-white font-mono">{vesselDetails.length}m</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Width:</span>
                    <p className="text-white font-mono">{vesselDetails.width}m</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Draft:</span>
                    <p className="text-white font-mono">{vesselDetails.draft}m</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Tonnage:</span>
                    <p className="text-white font-mono">{vesselDetails.tonnage} GT</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Built:</span>
                    <p className="text-white">{vesselDetails.built}</p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Call Sign:</span>
                    <p className="text-white font-mono">{vesselDetails.callsign}</p>
                  </div>
                </div>
              </div>

              {/* Historical Events */}
              <div className="p-3 bg-black/20 border border-white/10">
                <h3 className="text-xs font-bold uppercase mb-3 flex items-center gap-2">
                  <History className="w-4 h-4 text-[#00FF9D]" />
                  Historical Events
                </h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {vesselDetails.events && vesselDetails.events.length > 0 ? (
                    vesselDetails.events.map((event, idx) => (
                      <div key={idx} className="p-2 bg-black/30 border-l-2 border-[#00FF9D]/50">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-[#00FF9D] font-bold uppercase">{event.type}</span>
                          <span className="text-[10px] text-zinc-500 font-mono">{event.date}</span>
                        </div>
                        <p className="text-xs text-zinc-300">{event.description}</p>
                        {event.location && (
                          <p className="text-[10px] text-zinc-500 mt-1 font-mono">📍 {event.location}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-zinc-500 text-center py-4">No historical events recorded</p>
                  )}
                </div>
              </div>

              {/* Historical Track */}
              {historicalTrack.length > 0 && (
                <div className="p-3 bg-black/20 border border-white/10">
                  <h3 className="text-xs font-bold uppercase mb-2">Track History</h3>
                  <p className="text-xs text-zinc-400">{historicalTrack.length} positions recorded in last 24 hours</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-[#00FF9D] animate-spin" />
            </div>
          )}
        </div>
      )}

      {/* Maritime Map */}
      <div className="w-full h-full pt-24">
        <MapContainer
          center={[30, 0]}
          zoom={3}
          style={{ height: '100%', width: '100%' }}
          className="leaflet-container"
        >
          {/* OpenSeaMap Maritime Tiles */}
          <TileLayer
            attribution='&copy; <a href="https://www.openseamap.org/">OpenSeaMap</a>'
            url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
          />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Vessel Markers */}
          {filteredVessels.map((vessel, idx) => (
            <Marker
              key={idx}
              position={[vessel.lat, vessel.lon]}
              icon={createVesselIcon(vessel.type, vessel.heading)}
              eventHandlers={{
                click: () => handleVesselClick(vessel)
              }}
            >
              <Popup>
                <div className="text-white font-mono text-xs">
                  <p className="font-bold">{vessel.name}</p>
                  <p>Type: {vessel.type}</p>
                  <p>Speed: {vessel.speed} knots</p>
                  <p>Flag: {vessel.flag}</p>
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Historical Track Line */}
          {historicalTrack.length > 0 && (
            <Polyline
              positions={historicalTrack.map(point => [point.lat, point.lon])}
              color="#00FF9D"
              weight={2}
              opacity={0.6}
            />
          )}
          
          {/* Auto-zoom disabled - user has manual map control */}
        </MapContainer>
      </div>
    </div>
  );
}
